import { API_BASE } from '../apiBase';
import { fmtPhone, fmtPhoneInput } from '../lib/phone';
import JobsiteMiniMap from '../lib/JobsiteMiniMap';
// src/customers/CustomerProfile.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [projects, setProjects] = useState([]); // Linked Projects
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [editError, setEditError] = useState('');

  // Add history note states
  const [showAddNoteForm, setShowAddNoteForm] = useState(false);
  const [newNote, setNewNote] = useState({ date: '', employee: '', note: '' });
  const [noteError, setNoteError] = useState('');

  // Copied state
  const [copiedCell, setCopiedCell] = useState(null);

  const role = localStorage.getItem('role') || 'employee';
  const isOwner = role === 'superadmin';

  // Tabs (no Finances for customers)
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'team', label: 'Team' },
    { id: 'documents', label: 'Documents & Photos' },
  ];

  useEffect(() => {
    const fetchCustomer = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        setLoading(true);
        const customerRes = await axios.get(`${API_BASE}/customers/${id}`, {
          headers: { Authorization: token }
        });
        setCustomer(customerRes.data);

        // Fetch linked projects
        const projectsRes = await axios.get(`${API_BASE}/projects`, {
          headers: { Authorization: token }
        });
        const linked = projectsRes.data.filter(p => p.customer?._id === id || p.customer === id);
        setProjects(linked);
      } catch (err) {
        console.error('Fetch error:', err);
        setError('Failed to load customer details');
      } finally {
        setLoading(false);
      }
    };
    fetchCustomer();
  }, [id, navigate]);

  // Click-to-copy
  const copyToClipboard = async (text, field, index = null) => {
    try {
      await navigator.clipboard.writeText(text || '');
      setCopiedCell({ field, index });
      setTimeout(() => setCopiedCell(null), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Open edit modal
  const openEditModal = () => {
    setEditCustomer({
      name: customer.name || '',
      businessName: customer.businessName || '',
      type: customer.type || 'Homeowner',
      status: customer.status || 'Lead',
      primaryPhone: customer.primaryPhone || '',
      primaryEmail: customer.primaryEmail || '',
      secondaryPhone: customer.secondaryPhone || '',
      altEmail: customer.altEmail || '',
      billingAddress: {
        street: customer.billingAddress?.street || '',
        city: customer.billingAddress?.city || '',
        state: customer.billingAddress?.state || 'CA',
        zip: customer.billingAddress?.zip || ''
      },
      notes: customer.notes || '',
      tags: customer.tags?.join(', ') || '',
      customFields: customer.customFields ? { ...customer.customFields } : {}
    });
    setEditError('');
    setShowEditModal(true);
  };

  // Handle edit input changes
  const handleEditChange = (e, path = null) => {
    const { name } = e.target;
    // Phones auto-format to xxx-xxx-xxxx as you type — app-wide standard.
    const value = /phone/i.test(name) ? fmtPhoneInput(e.target.value) : e.target.value;
    if (path === 'customFields') {
      setEditCustomer(prev => ({
        ...prev,
        customFields: {
          ...prev.customFields,
          [name]: value
        }
      }));
    } else if (path) {
      setEditCustomer(prev => ({
        ...prev,
        [path]: { ...prev[path], [name]: value }
      }));
    } else {
      setEditCustomer(prev => ({ ...prev, [name]: value }));
    }
  };

  // Save edited customer
  const handleSaveEdit = async () => {
    if (!editCustomer.name.trim()) {
      setEditError('Name is required');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...editCustomer,
        tags: editCustomer.tags.split(',').map(t => t.trim()).filter(Boolean),
        customFields: editCustomer.customFields
      };

      const res = await axios.put(`${API_BASE}/customers/${id}`, payload, {
        headers: { Authorization: token }
      });

      setCustomer(res.data);
      setShowEditModal(false);
      setEditError('');
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update customer');
    }
  };

  // Add new history note
  const handleAddNote = async () => {
    if (!newNote.note.trim()) {
      setNoteError('Note is required');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const updatedHistory = [
        ...(customer.relationshipHistory || []),
        {
          date: newNote.date || new Date().toISOString(),
          employee: newNote.employee || 'Current User',
          note: newNote.note.trim()
        }
      ];

      const res = await axios.put(`${API_BASE}/customers/${id}`, {
        relationshipHistory: updatedHistory
      }, {
        headers: { Authorization: token }
      });

      setCustomer(res.data);
      setNewNote({ date: '', employee: '', note: '' });
      setNoteError('');
      setShowAddNoteForm(false);
    } catch (err) {
      setNoteError('Failed to add note');
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-950 text-zinc-100 p-8">Loading customer...</div>;
  if (error) return <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 text-red-400">{error}</div>;
  if (!customer) return <div className="min-h-screen bg-gray-950 text-zinc-100 p-8">Customer not found</div>;

  return (
    <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 space-y-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            <span
              className="cursor-pointer relative group inline-block"
              onClick={() => copyToClipboard(customer.name, 'name')}
            >
              {customer.name}
              {copiedCell?.field === 'name' && (
                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">
                  Copied!
                </span>
              )}
            </span>
          </h1>
          {customer.businessName && (
            <p className="text-lg text-zinc-200 font-medium mt-1">{customer.businessName}</p>
          )}
          <p className="text-zinc-400 mt-1">
            {customer.type} • {customer.status}
          </p>
        </div>
        <div className="flex gap-4">
          <button
            onClick={() => navigate('/customers')}
            className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-medium transition-all"
          >
            Back to List
          </button>
          <button
            onClick={openEditModal}
            className="px-6 py-3 bg-indigo-700 hover:bg-indigo-600 text-white rounded-xl font-medium shadow-md transition-all hover:shadow-lg"
          >
            Edit Profile
          </button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-gray-800">
        <nav className="flex space-x-8 overflow-x-auto pb-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-4 px-2 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-indigo-400 border-b-2 border-indigo-500'
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-10">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              {/* Contact Info Card */}
              <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-8 shadow-2xl">
                <h2 className="text-xl font-bold mb-6">Contact Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-zinc-400 text-sm">Business Name</p>
                    <p className="text-lg font-medium mt-1">
                      <span className="cursor-pointer relative group inline-block" onClick={() => copyToClipboard(customer.businessName || '', 'businessName')}>
                        {customer.businessName || 'N/A'}
                        {copiedCell?.field === 'businessName' && (<span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">Copied!</span>)}
                      </span>
                    </p>
                  </div>
                  {/* Primary Phone, Email, etc. - unchanged */}
                  <div>
                    <p className="text-zinc-400 text-sm">Primary Phone</p>
                    <p className="text-lg font-medium mt-1">
                      <span className="cursor-pointer relative group inline-block" onClick={() => copyToClipboard(fmtPhone(customer.primaryPhone) || '', 'primaryPhone')}>
                        {fmtPhone(customer.primaryPhone) || 'N/A'}
                        {copiedCell?.field === 'primaryPhone' && (<span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">Copied!</span>)}
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Primary Email</p>
                    <p className="text-lg font-medium mt-1">
                      <span className="cursor-pointer relative group inline-block" onClick={() => copyToClipboard(customer.primaryEmail || '', 'primaryEmail')}>
                        {customer.primaryEmail || 'N/A'}
                        {copiedCell?.field === 'primaryEmail' && (<span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">Copied!</span>)}
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Secondary Phone</p>
                    <p className="text-lg font-medium mt-1">
                      <span className="cursor-pointer relative group inline-block" onClick={() => copyToClipboard(customer.secondaryPhone || '', 'secondaryPhone')}>
                        {customer.secondaryPhone || 'N/A'}
                        {copiedCell?.field === 'secondaryPhone' && (<span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">Copied!</span>)}
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Alternate Email</p>
                    <p className="text-lg font-medium mt-1">
                      <span className="cursor-pointer relative group inline-block" onClick={() => copyToClipboard(customer.altEmail || '', 'altEmail')}>
                        {customer.altEmail || 'N/A'}
                        {copiedCell?.field === 'altEmail' && (<span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">Copied!</span>)}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Billing / Project Address — click any line to copy; map dot below */}
              <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-8 shadow-2xl">
                <h2 className="text-xl font-bold mb-6">Billing / Project Address</h2>
                {(() => {
                  const a = customer.billingAddress || {};
                  const l2 = [[a.city, a.state].filter(Boolean).join(', '), a.zip].filter(Boolean).join(' ');
                  const full = [a.street, l2, (a.country && a.country !== 'USA') ? a.country : ''].filter(Boolean).join(', ');
                  return (
                    <div className="space-y-2 text-lg">
                      <p>
                        <span className="cursor-pointer relative group inline-block" onClick={() => a.street && copyToClipboard(a.street, 'addr1')}>
                          {a.street || 'N/A'}
                          {copiedCell?.field === 'addr1' && (<span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">Copied!</span>)}
                        </span>
                      </p>
                      <p>
                        <span className="cursor-pointer relative group inline-block" onClick={() => l2 && copyToClipboard(l2, 'addr2')}>
                          {a.city || 'N/A'}, {a.state || 'N/A'} {a.zip || 'N/A'}
                          {copiedCell?.field === 'addr2' && (<span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">Copied!</span>)}
                        </span>
                      </p>
                      <p>
                        <span className="cursor-pointer relative group inline-block" onClick={() => a.country && copyToClipboard(a.country, 'addrCountry')}>
                          {a.country || 'N/A'}
                          {copiedCell?.field === 'addrCountry' && (<span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">Copied!</span>)}
                        </span>
                      </p>
                      {full && <JobsiteMiniMap className="mt-4" address={full} />}
                    </div>
                  );
                })()}
              </div>

              {/* Notes & Tags - unchanged */}
              <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-8 shadow-2xl">
                <h2 className="text-xl font-bold mb-6">Notes & Tags</h2>
                <div className="flex flex-wrap gap-2 mb-6">
                  {customer.tags?.length > 0 ? customer.tags.map((tag, i) => (
                    <span key={i} className="px-3 py-1 bg-indigo-700/40 text-indigo-300 rounded-full text-sm">
                      {tag}
                    </span>
                  )) : <span className="text-zinc-500">N/A</span>}
                </div>
                <p className="text-zinc-300 whitespace-pre-line">{customer.notes || 'N/A'}</p>
              </div>
            </div>

            <div className="space-y-8">
              {/* Custom Details */}
              <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-6 shadow-2xl">
                <h2 className="text-xl font-bold mb-6">Custom Details</h2>
                <div className="space-y-4 text-sm">
                  {customer.customFields && Object.keys(customer.customFields).length > 0 ? (
                    Object.entries(customer.customFields).map(([key, value], index) => (
                      <div key={key}>
                        <p className="text-zinc-400 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                        <p className="font-medium">{value}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-zinc-500">N/A</p>
                  )}
                </div>
              </div>

              {/* Linked Projects */}
              <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-8 shadow-2xl">
                <h2 className="text-xl font-bold mb-6">Linked Projects</h2>
                {projects.length > 0 ? (
                  <ul className="space-y-3">
                    {projects.map(proj => (
                      <li key={proj._id}>
                        <button
                          onClick={() => navigate(`/projects/${proj._id}`)}
                          className="text-zinc-200 hover:text-indigo-400 transition-colors text-left block w-full text-lg font-medium"
                        >
                          {proj.title} <span className="text-zinc-500 text-sm">({proj.poNumber})</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-zinc-500">No linked projects yet.</p>
                )}
              </div>

              {/* Relationship History */}
              <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-6 shadow-2xl">
                <h2 className="text-xl font-bold mb-6">Relationship History</h2>
                <div className="space-y-4">
                  {customer.relationshipHistory?.length > 0 ? (
                    customer.relationshipHistory.map((note, i) => (
                      <div key={i} className="border-l-4 border-indigo-600 pl-4">
                        <p className="text-sm text-zinc-400">
                          {new Date(note.date).toLocaleDateString()} • {note.employee || 'Unknown'}
                        </p>
                        <p className="mt-1">{note.note}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-zinc-500">N/A</p>
                  )}
                </div>

                {/* Add History Note */}
                {!showAddNoteForm ? (
                  <button
                    onClick={() => setShowAddNoteForm(true)}
                    className="mt-4 text-indigo-400 hover:text-indigo-300 text-sm"
                  >
                    + Add History Note
                  </button>
                ) : (
                  <div className="mt-4 space-y-4">
                    <input
                      type="date"
                      value={newNote.date}
                      onChange={(e) => setNewNote({ ...newNote, date: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                    />
                    <input
                      placeholder="Employee Name"
                      value={newNote.employee}
                      onChange={(e) => setNewNote({ ...newNote, employee: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                    />
                    <textarea
                      placeholder="Note details"
                      value={newNote.note}
                      onChange={(e) => setNewNote({ ...newNote, note: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                    />
                    {noteError && <p className="text-red-400 text-sm">{noteError}</p>}
                    <div className="flex gap-4">
                      <button
                        onClick={() => setShowAddNoteForm(false)}
                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-xl text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddNote}
                        className="px-4 py-2 bg-indigo-700 hover:bg-indigo-600 rounded-xl text-sm"
                      >
                        Add Note
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Other tabs unchanged */}
        {activeTab === 'timeline' && (
          <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-8 shadow-2xl">
            <h2 className="text-xl font-bold mb-6">Project Timeline</h2>
            <p className="text-zinc-500">Coming soon...</p>
          </div>
        )}
        {activeTab === 'team' && (
          <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-8 shadow-2xl">
            <h2 className="text-xl font-bold mb-6">Assigned Team</h2>
            <p className="text-zinc-500">Coming soon...</p>
          </div>
        )}
        {activeTab === 'documents' && (
          <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-8 shadow-2xl">
            <h2 className="text-xl font-bold mb-6">Documents & Photos</h2>
            <p className="text-zinc-400">Coming soon...</p>
          </div>
        )}
      </div>

      {/* ==================== EDIT PROFILE MODAL ==================== */}
      {showEditModal && editCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl p-8 w-full max-w-2xl border border-gray-700 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">Edit Customer Profile</h2>
            {editError && <p className="text-red-400 mb-4">{editError}</p>}

            <div className="space-y-6">
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Name</label>
                <input
                  name="name"
                  value={editCustomer.name}
                  onChange={handleEditChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                />
              </div>

              <div>
                <label className="block text-zinc-400 text-sm mb-1">Business Name <span className="text-zinc-600">(optional)</span></label>
                <input
                  name="businessName"
                  value={editCustomer.businessName}
                  onChange={handleEditChange}
                  placeholder="e.g. McDonald's LLC — leave blank for individuals"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200 placeholder-zinc-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Type</label>
                  <select
                    name="type"
                    value={editCustomer.type}
                    onChange={handleEditChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                  >
                    <option value="Homeowner">Homeowner</option>
                    <option value="Business">Business</option>
                    <option value="Vendor">Vendor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Status</label>
                  <select
                    name="status"
                    value={editCustomer.status}
                    onChange={handleEditChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                  >
                    <option value="Lead">Lead</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Primary Phone</label>
                  <input
                    name="primaryPhone"
                    value={editCustomer.primaryPhone}
                    onChange={handleEditChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Primary Email</label>
                  <input
                    name="primaryEmail"
                    value={editCustomer.primaryEmail}
                    onChange={handleEditChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Secondary Phone</label>
                  <input
                    name="secondaryPhone"
                    value={editCustomer.secondaryPhone}
                    onChange={handleEditChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Alternate Email</label>
                  <input
                    name="altEmail"
                    value={editCustomer.altEmail}
                    onChange={handleEditChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                  />
                </div>
              </div>

              {/* Billing Address */}
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Billing Address</label>
                <div className="space-y-3">
                  <input
                    name="street"
                    placeholder="Street"
                    value={editCustomer.billingAddress.street}
                    onChange={(e) => handleEditChange(e, 'billingAddress')}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <input
                      name="city"
                      placeholder="City"
                      value={editCustomer.billingAddress.city}
                      onChange={(e) => handleEditChange(e, 'billingAddress')}
                      className="bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-zinc-200"
                    />
                    <input
                      name="state"
                      placeholder="State"
                      value={editCustomer.billingAddress.state}
                      onChange={(e) => handleEditChange(e, 'billingAddress')}
                      className="bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-zinc-200"
                    />
                    <input
                      name="zip"
                      placeholder="ZIP"
                      value={editCustomer.billingAddress.zip}
                      onChange={(e) => handleEditChange(e, 'billingAddress')}
                      className="bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-zinc-200"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-zinc-400 text-sm mb-1">Notes</label>
                <textarea
                  name="notes"
                  value={editCustomer.notes}
                  onChange={handleEditChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                />
              </div>

              <div>
                <label className="block text-zinc-400 text-sm mb-1">Tags (comma separated)</label>
                <input
                  name="tags"
                  value={editCustomer.tags}
                  onChange={handleEditChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                  placeholder="VIP, Repeat, High Value"
                />
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-8">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-medium transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-6 py-3 bg-indigo-700 hover:bg-indigo-600 text-white rounded-xl font-medium shadow-md transition-all hover:shadow-lg"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerProfile;